import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useToast } from "@hightouch/htui";
import { useLocation } from "src/router";
import { useUser } from "src/contexts/user-context";
import { useRegionConfig } from "src/utils/use-region-config";
import { track } from "src/lib/analytics";
import { insertTextAtCursor } from "src/utils/chat-newline-shortcut";

const MAX_BYTES = 10 * 1024 * 1024;
const MAX_SECONDS = 180;
const MICROPHONE_BLOCKED_TITLE = "Microphone access is blocked";
const MICROPHONE_BLOCKED_DETAIL = "Enable it in your browser's site settings.";
const MICROPHONE_BLOCKED_MESSAGE = `${MICROPHONE_BLOCKED_TITLE}. ${MICROPHONE_BLOCKED_DETAIL}`;
const MIME_TYPES = ["audio/webm;codecs=opus", "audio/mp4"];
type State = "idle" | "requesting" | "recording" | "transcribing";
type Take = {
  context: string;
  draft: string;
  controller: AbortController;
  stop: () => void;
  release: () => void;
};

export function useComposerDictation({
  textareaRef,
  value,
  disabled,
  contextKey,
}: {
  textareaRef: RefObject<HTMLTextAreaElement>;
  value: string;
  disabled: boolean;
  contextKey?: string;
}) {
  const { agentDictation } = useFlags<{ agentDictation?: boolean }>();
  const { toast } = useToast();
  const { workspace } = useUser();
  const { apiEndpoint } = useRegionConfig();
  const location = useLocation();
  const enabled = agentDictation === true && Boolean(workspace);
  const context = `${workspace?.id}:${location.key}:${contextKey ?? ""}`;
  const takeRef = useRef<Take>();
  const dictatedRef = useRef(false);
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<{ message: string; blocked: boolean }>();
  const [startedAt, setStartedAt] = useState<number>();
  const [analyser, setAnalyser] = useState<AnalyserNode>();
  const mimeType =
    typeof MediaRecorder !== "undefined"
      ? MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type))
      : undefined;
  const supported = Boolean(mimeType && navigator.mediaDevices?.getUserMedia);

  const cancel = useCallback(() => {
    const take = takeRef.current;
    takeRef.current = undefined;
    take?.controller.abort();
    take?.stop();
    take?.release();
    setAnalyser(undefined);
    setState("idle");
    setStartedAt(undefined);
  }, []);
  useEffect(() => cancel, [cancel]);
  useEffect(() => {
    if (!value.trim()) dictatedRef.current = false;
  }, [value]);
  useEffect(() => {
    const take = takeRef.current;
    if (
      take &&
      (take.context !== context || take.draft !== value || !enabled || disabled)
    )
      cancel();
  }, [context, value, enabled, disabled, cancel]);

  const trackSubmit = useCallback(() => {
    if (!dictatedRef.current) return;
    dictatedRef.current = false;
    track("Agents Prompt Submitted With Dictation", {
      origin_page: location.pathname,
    });
  }, [location.pathname]);

  const start = async () => {
    const textarea = textareaRef.current;
    if (
      takeRef.current ||
      !enabled ||
      disabled ||
      !supported ||
      !mimeType ||
      !workspace ||
      !textarea
    )
      return;
    const cursor = textarea.selectionEnd;
    const take: Take = {
      context,
      draft: textarea.value,
      controller: new AbortController(),
      stop: () => {},
      release: () => {},
    };
    takeRef.current = take;
    const current = () => takeRef.current === take;
    setState("requesting");
    setError(undefined);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!current()) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      let audioContext: AudioContext | undefined;
      let timeout: ReturnType<typeof setTimeout> | undefined;
      take.release = () => {
        clearTimeout(timeout);
        stream.getTracks().forEach((track) => track.stop());
        if (audioContext && audioContext.state !== "closed") {
          void audioContext
            .close()
            .catch(() =>
              console.warn("Could not close dictation audio context"),
            );
          audioContext = undefined;
        }
      };
      const recorder = new MediaRecorder(stream, { mimeType });
      const audio = await new Promise<Blob>((resolve, reject) => {
        recorder.ondataavailable = (event) => resolve(event.data);
        recorder.onerror = () =>
          reject(
            new Error("Recording stopped unexpectedly. Try recording again."),
          );
        take.stop = () => {
          if (recorder.state !== "recording") return;
          recorder.stop();
          if (current()) setState("transcribing");
        };
        recorder.start();
        setState("recording");
        track("Agents Dictation Started", { origin_page: location.pathname });
        setStartedAt(Date.now());
        timeout = setTimeout(take.stop, MAX_SECONDS * 1000);
        // Visualization must not block recording if the browser suspends Web Audio.
        try {
          if (typeof AudioContext !== "undefined") {
            audioContext = new AudioContext();
            const node = audioContext.createAnalyser();
            node.fftSize = 256;
            audioContext.createMediaStreamSource(stream).connect(node);
            setAnalyser(node);
            if (audioContext.state === "suspended") {
              void audioContext.resume().catch(() => {
                console.warn("Could not resume dictation waveform");
              });
            }
          }
        } catch {
          console.warn("Could not initialize dictation waveform");
        }
      });
      if (!current()) return;
      take.release();
      setAnalyser(undefined);
      setState("transcribing");
      if (!audio.size)
        throw new Error("No audio was recorded. Try recording again.");
      if (audio.size > MAX_BYTES)
        throw new Error("Recording is too large. Try a shorter recording.");
      const response = await fetch(`${apiEndpoint}/agents/transcribe`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": audio.type,
          "X-Hightouch-Requested-Workspace-Id": String(workspace.id),
        },
        body: audio,
        signal: take.controller.signal,
      });
      if (!response.ok) {
        let failure: unknown;
        try {
          failure = await response.json();
        } catch {
          console.warn("Could not read dictation error response");
        }
        throw new Error(
          failure &&
          typeof failure === "object" &&
          "error" in failure &&
          typeof failure.error === "string" &&
          failure.error.trim()
            ? failure.error
            : "Could not transcribe audio. Try recording again.",
        );
      }
      const result: unknown = await response.json();
      if (
        !result ||
        typeof result !== "object" ||
        !("text" in result) ||
        typeof result.text !== "string"
      )
        throw new Error("Could not transcribe audio. Try recording again.");
      const text = result.text.trim();
      if (!text)
        throw new Error("No speech was detected. Try recording again.");
      if (!current()) return;
      // Native insertion requires React to have committed the editable textarea.
      flushSync(() => setState("idle"));
      if (!current() || textarea.value !== take.draft) return;
      const before = take.draft.slice(0, cursor);
      const after = take.draft.slice(cursor);
      const leading =
        before && !/\s$/.test(before) && !/^[,.;:!?)]/.test(text) ? " " : "";
      const trailing = after && !/^[\s,.;:!?)]/.test(after) ? " " : "";
      textarea.focus();
      insertTextAtCursor(textarea, leading + text + trailing, {
        start: cursor,
        end: cursor,
      });
      if (textarea.value === take.draft) {
        throw new Error(
          "Could not insert the transcript. Try recording again.",
        );
      }
      dictatedRef.current = true;
    } catch (err) {
      if (current()) {
        const denied =
          err instanceof DOMException &&
          (err.name === "NotAllowedError" || err.name === "SecurityError");
        setError({
          blocked: denied,
          message: denied
            ? MICROPHONE_BLOCKED_MESSAGE
            : err instanceof Error
              ? err.message
              : "Could not transcribe audio. Try again.",
        });
        if (denied) {
          toast({
            id: "dictation-microphone-blocked",
            title: MICROPHONE_BLOCKED_TITLE,
            message: MICROPHONE_BLOCKED_DETAIL,
            variant: "error",
          });
        }
      }
    } finally {
      if (current()) cancel();
    }
  };
  return {
    state,
    error,
    startedAt,
    analyser,
    supported,
    enabled,
    disabled,
    busy: state !== "idle",
    start,
    stop: () => takeRef.current?.stop(),
    cancel,
    trackSubmit,
  };
}

export type ComposerDictation = ReturnType<typeof useComposerDictation>;
